import { Button, Carousel } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import RegisterCard from "../components/RegisterCard";
import { useEffect, useRef } from "react";
import {
  CartIcon,
  FacebookIcon,
  RightArrowIcon,
  StarIcon,
  PersonIcon,
  PlatformIcon,
  LikeIcon,
  XIcon,
  YouTubeIcon,
  PinterestIcon,
  BankIcon,
  InputIcon,
  InsuranceIcon,
  OffTakerIcon,
  TransporterIcon,
} from "../components/icons";

import tractor from "../assets/images/tractor.png";
import farmer from "../assets/images/farmer.png";
import potato from "../assets/images/potato.png";
import tomato from "../assets/images/tomato.png";
import onion from "../assets/images/onion.png";
import poultry from "../assets/images/poultry.png";
import eggs from "../assets/images/eggs.png";
import groceries from "../assets/images/groceries.png";

/** Hero carousel: single image, or 2×2 grid with left column spanning two rows (collage) */
type HeroCarouselItem =
  | {
      layout: "collage";
      title: string;
      desc: string;
      /** Large left panel — e.g. poultry.png */
      left: { src: string; alt: string };
      topRight: { src: string; alt: string };
      bottomRight: { src: string; alt: string };
    }
  | {
      layout: "image";
      src: string;
      title: string;
      desc: string;
    };

/** Full-width feel: cap at large desktop, modest gutters so cards aren’t squeezed */
const sectionInner =
  "w-full max-w-[min(100%,1920px)] mx-auto px-3 sm:px-4 md:px-6 lg:px-8";

/** Registration row: minimal side padding so five cards use maximum horizontal space */
const registerSectionInner =
  "w-full max-w-[min(100%,1920px)] mx-auto px-2 sm:px-3 md:px-5 lg:px-6";

function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const registerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const id = location.hash.replace(/^#/, "");
    if (!id) return;
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  const carouselItems: HeroCarouselItem[] = [
    {
      layout: "collage",
      title: "Farm-fresh variety",
      desc: "Poultry, eggs, and potatoes from trusted producers",
      left: { src: poultry, alt: "Poultry" },
      topRight: { src: eggs, alt: "Fresh eggs" },
      bottomRight: { src: potato, alt: "Potatoes" },
    },
    {
      layout: "image",
      src: tomato,
      title: "Ripe Tomatoes",
      desc: "Harvested at peak freshness",
    },
    {
      layout: "image",
      src: onion,
      title: "Organic Onions",
      desc: "Naturally grown",
    },
    {
      layout: "image",
      src: poultry,
      title: "Free-Range Poultry",
      desc: "Healthy, ethical farming",
    },
    {
      layout: "image",
      src: eggs,
      title: "Fresh Eggs",
      desc: "High-protein, farm-fresh",
    },
    {
      layout: "image",
      src: groceries,
      title: "Farm Groceries",
      desc: "Direct from source to table",
    },
  ];

  const benefits = [
    {
      icon: <StarIcon size={48} color="#059669" />,
      title: "Reliable & Trustworthy",
      description:
        "Orchestrates diverse agricultural value chain actors with signed contracts and secure, regularly updated platform.",
    },
    {
      icon: <PersonIcon size={48} color="#059669" />,
      title: "Professional & Experienced",
      description:
        "Built by digital agriculture experts and backed by seasoned agronomists who understand farming practices.",
    },
    {
      icon: <PlatformIcon size={48} color="#059669" />,
      title: "Scalable Platform",
      description:
        "Automates interactions across the value chain with easy subscription and customization for new sectors.",
    },
    {
      icon: <LikeIcon size={48} color="#059669" />,
      title: "Fresh Farm Products",
      description:
        "Organizes multiple value chains including potatoes, barley, tomatoes, poultry, eggs, and groceries.",
    },
  ];

  const registerOptions = [
    {
      href: "/subscribe/MICRO-FINANCE",
      title: "Banks",
      icon: <BankIcon size={52} color="#C2410C" />,
    },
    {
      href: "/subscribe/MULTI_PURPOSE_COOPERATIVE",
      title: "Input Suppliers",
      icon: <InputIcon size={52} color="#C2410C" />,
    },
    {
      href: "/subscribe/INSURANCE",
      title: "Insurance",
      icon: <InsuranceIcon size={52} color="#C2410C" />,
    },
    {
      href: "/subscribe/OFF_TAKERS",
      title: "Off Taker",
      icon: <OffTakerIcon size={52} color="#C2410C" />,
    },
    {
      href: "/subscribe/TRANSPORTERS",
      title: "Transporter",
      icon: <TransporterIcon size={52} color="#C2410C" />,
    },
  ];

  const challenges = [
    "No control over input sourcing (quantity, variety, quality)",
    "Difficulty tracking production lifecycle through harvest",
    "Unable to trace the grower of purchased products",
    "Lack of formal contracts with growers",
    "Cumbersome input sourcing processes",
    "Challenge tracing farm gate prices",
    "Planning challenges for future production inputs",
  ];

  return (
    <div className="bg-white">
      {/* Hero Section - Full width with gradient background */}
      <section
        id="home"
        className="relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-amber-50 scroll-mt-24"
      >
        <div className={`${sectionInner} py-12 md:py-20`}>
          <div className="flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-16">
            {/* Left Content */}
            <div className="w-full lg:w-1/2 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Welcome to Shega Fre
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Organizing <span className="text-green-600">Agricultural</span>
                <br />
                Value Chain
              </h1>

              <p className="text-gray-600 text-lg leading-relaxed mt-6 max-w-xl mx-auto lg:mx-0">
                Empowering farmers, agribusinesses, and stakeholders with
                knowledge and resources to optimize operations and maximize
                potential.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mt-8">
                <button
                  onClick={() =>
                    registerRef.current?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="px-8 py-3.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <CartIcon size={18} color="#fff" />
                  Subscribe now
                </button>

                <Link
                  to="/learn-more"
                  className="px-8 py-3.5 bg-white border-2 border-gray-200 hover:border-green-500 text-gray-700 font-semibold rounded-full transition-all duration-300 flex items-center gap-2"
                >
                  Learn more
                  <RightArrowIcon size={18} color="currentColor" />
                </Link>
              </div>

              {/* Value highlights — no placeholder metrics */}
              <div className="mt-12 grid grid-cols-1 gap-4 border-t border-gray-100 pt-8 sm:grid-cols-3 sm:gap-5">
                <div className="flex min-h-[112px] flex-col justify-center rounded-2xl border border-gray-100/80 bg-gray-50 px-5 py-5 text-left shadow-sm">
                  <div className="text-base font-semibold text-gray-800">
                    End-to-end chain
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    From inputs to market
                  </div>
                </div>
                <div className="flex min-h-[112px] flex-col justify-center rounded-2xl border border-gray-100/80 bg-gray-50 px-5 py-5 text-left shadow-sm">
                  <div className="text-base font-semibold text-gray-800">
                    Contract-based
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    Clear roles & commitments
                  </div>
                </div>
                <div className="flex min-h-[112px] flex-col justify-center rounded-2xl border border-gray-100/80 bg-gray-50 px-5 py-5 text-left shadow-sm">
                  <div className="text-base font-semibold text-gray-800">
                    Multi-sector
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    Crops, poultry & more
                  </div>
                </div>
              </div>
            </div>

            {/* Right Carousel */}
            <div className="w-full lg:w-1/2">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-green-200 rounded-full opacity-50 blur-2xl"></div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-amber-200 rounded-full opacity-50 blur-2xl"></div>
                <Carousel
                  autoplay
                  autoplaySpeed={4000}
                  effect="fade"
                  className="rounded-3xl overflow-hidden shadow-2xl"
                >
                  {carouselItems.map((item, idx) => (
                    <div key={idx} className="relative">
                      {item.layout === "collage" ? (
                        <div className="grid h-[360px] grid-cols-2 grid-rows-2 gap-2 bg-white p-2 md:h-[460px]">
                          <div className="relative row-span-2 min-h-0 overflow-hidden rounded-xl">
                            <img
                              src={item.left.src}
                              alt={item.left.alt}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="relative min-h-0 overflow-hidden rounded-xl">
                            <img
                              src={item.topRight.src}
                              alt={item.topRight.alt}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="relative min-h-0 overflow-hidden rounded-xl">
                            <img
                              src={item.bottomRight.src}
                              alt={item.bottomRight.alt}
                              className="h-full w-full object-cover"
                            />
                          </div>
                        </div>
                      ) : (
                        <img
                          src={item.src}
                          alt={item.title}
                          className="h-[360px] w-full object-cover md:h-[460px]"
                        />
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                        <h3 className="text-white text-xl font-bold">
                          {item.title}
                        </h3>
                        <p className="text-white/80 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </Carousel>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section — image column stretches to match benefit cards on large screens */}
      <section
        id="products"
        className={`${sectionInner} py-16 md:py-20 scroll-mt-24`}
      >
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-green-600 font-semibold text-sm uppercase tracking-wide">
            Why Choose Us
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-2">
            Why SHEGA-FRE?
          </h2>
          <p className="text-gray-500 mt-4">
            We provide agriculture-related materials and facilitate connections
            across the entire value chain.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-12 lg:items-stretch lg:gap-10">
          <div className="flex min-h-[280px] lg:col-span-5 lg:min-h-0">
            <div className="relative flex w-full flex-1 overflow-hidden rounded-2xl shadow-xl group">
              <img
                src={tractor}
                alt="Modern agriculture — tractor in the field"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
              <div className="relative mt-auto flex w-full items-end p-6 md:p-8">
                <div>
                  <h3 className="text-white text-xl font-bold">
                    Modern Agriculture
                  </h3>
                  <p className="text-white/85 text-sm mt-1">
                    Technology meets tradition
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:col-span-7 lg:gap-6">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-6 transition-all duration-300 hover:border-green-200 hover:shadow-lg group"
              >
                <div className="mb-4 transform transition duration-300 group-hover:scale-105">
                  {benefit.icon}
                </div>
                <h3 className="font-bold text-xl mb-2 text-gray-800">
                  {benefit.title}
                </h3>
                <p className="text-gray-500 leading-relaxed grow">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Links Bar */}
      <div className="bg-green-600">
        <div className={sectionInner}>
          <div className="flex flex-wrap justify-center md:justify-around items-center py-3 gap-4">
            <Link
              to="/facebook"
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <FacebookIcon size={20} color="currentColor" />
              <span className="text-sm font-medium">Facebook</span>
            </Link>
            <Link
              to="/twitter"
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <XIcon size={20} color="currentColor" />
              <span className="text-sm font-medium">Twitter</span>
            </Link>
            <Link
              to="/youtube"
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <YouTubeIcon size={20} color="currentColor" />
              <span className="text-sm font-medium">YouTube</span>
            </Link>
            <Link
              to="/pinterest"
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <PinterestIcon size={20} color="currentColor" />
              <span className="text-sm font-medium">Pinterest</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Registration Section */}
      <section
        id="locations"
        className={`${registerSectionInner} py-16 md:py-20 scroll-mt-24`}
        ref={registerRef}
      >
        <div className="mx-auto mb-10 max-w-3xl text-center md:mb-12">
          <span className="text-green-600 font-semibold text-sm uppercase tracking-wide">
            Get Started
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-2">
            Register as a value chain actor
          </h2>
          <p className="text-gray-500 mt-4">
            Choose your role and subscribe—connect with others across the
            agricultural value chain.
          </p>
        </div>

        <div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-5 md:grid-cols-3 md:gap-4 lg:grid-cols-5 lg:gap-4 xl:gap-5">
          {registerOptions.map((option, idx) => (
            <RegisterCard
              key={idx}
              href={option.href}
              title={option.title}
              icon={option.icon}
            />
          ))}
        </div>
      </section>

      {/* Challenges Section */}
      <section
        id="farmers"
        className="bg-gradient-to-br from-gray-50 to-white py-16 md:py-20 scroll-mt-24"
      >
        <div className={sectionInner}>
          <div className="flex flex-col-reverse lg:flex-row gap-12 items-center">
            <div className="w-full lg:w-1/2">
              <img
                src={farmer}
                alt="farmer"
                className="w-full rounded-2xl shadow-xl object-cover"
              />
            </div>

            <div className="w-full lg:w-1/2">
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wide">
                Challenges
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-2 leading-tight">
                Are you an off-taker facing these challenges?
              </h2>

              <div className="grid grid-cols-1 gap-3 mt-6">
                {challenges.map((challenge, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition"
                  >
                    <svg
                      className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-gray-700 text-sm">{challenge}</span>
                  </div>
                ))}
              </div>

              <div className="bg-green-50 p-5 rounded-xl border-l-4 border-green-500 mt-6">
                <p className="text-gray-700">
                  If you want to address all these challenges, you're in the
                  right place. Register here to start controlling your input
                  source, or call our call center at{" "}
                  <strong className="text-green-600 text-lg">8500</strong> for
                  more information.
                </p>
              </div>

              <Button
                onClick={() => navigate("/subscribe")}
                type="primary"
                className="mt-6 px-8 py-3 h-auto bg-green-600 hover:bg-green-700 border-none rounded-xl font-semibold shadow-md"
              >
                Register now →
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <div className="bg-green-700 py-12">
        <div className={`${sectionInner} text-center`}>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to transform agriculture?
          </h3>
          <p className="text-green-100 mb-6 max-w-lg mx-auto">
            Whether you farm, finance, or move product—Shega Fre helps you work
            with clarity across the value chain.
          </p>
          <button
            onClick={() =>
              registerRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            className="px-8 py-3 bg-white text-green-700 font-semibold rounded-full hover:bg-gray-100 transition shadow-lg"
          >
            Get Started Today
          </button>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
